// frontend/src/components/shared/AppVersion.tsx

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { cn } from '@/lib/utils';

interface VersionResponse {
  version: string;
  commit: string;
  build_date: string;
}

export function AppVersion() {
  const { data } = useQuery({
    queryKey: ['appVersion'],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await axios.get<VersionResponse>(`${baseUrl}/health/version`);
      const environment = baseUrl.includes('staging')
        ? 'staging'
        : baseUrl.includes('production')
          ? 'production'
          : 'development';
      return {
        ...response.data,
        environment: environment as 'staging' | 'production' | 'development' | 'testing',
      };
    },
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  if (!data) {
    return null;
  }

  const shortCommit = data.commit !== 'dev' ? data.commit.slice(0, 7) : null;
  const buildDate =
    data.build_date !== 'unknown'
      ? new Date(data.build_date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : null;

  let tagContent: string | null = null;
  let tagClasses = '';

  switch (data.environment) {
    case 'development':
    case 'testing':
      tagContent = 'Local Dev';
      tagClasses = 'text-blue-500 font-semibold';
      break;
    case 'staging': {
      const parts = ['Staging'];
      if (shortCommit) parts.push(shortCommit);
      if (buildDate) parts.push(buildDate);
      tagContent = parts.join(' · ');
      tagClasses = 'text-amber-500 font-semibold';
      break;
    }
    case 'production': {
      const parts: string[] = [];
      if (shortCommit) parts.push(shortCommit);
      if (buildDate) parts.push(buildDate);
      tagContent = parts.length > 0 ? parts.join(' · ') : null;
      tagClasses = 'text-muted-foreground';
      break;
    }
    default:
      return null;
  }

  if (!tagContent) return null;

  return <span className={cn('text-xs', tagClasses)}>{tagContent}</span>;
}
